import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Col,
  Drawer,
  Form,
  Row,
  Select,
} from 'antd';
import PropTypes from 'prop-types';
import pdfMake from 'pdfmake';

import { currencyFormatter, percentFormatter } from '../../../helpers/inputParsers';
import { isNullOrUndefined } from '../../../helpers/helpers';

import ScheduleOfValuesPDFPreviewForm from './ScheduleOfValuesPDFPreviewForm';
import {
  getBase64ImageFromURL,
  getTableRow,
  getSovTableRows,
  getChangeOrderSummary,
  getNonHoldbackSummary,
  getBasePdfDefinition,
  getSubContractCOHeader,
  roundToCents,
  getCurrencyFontSize,
  calculateTextWidth,
  buildTableWidths,
  LANDSCAPE_MAX_VALUES,
  PORTRAIT_MAX_VALUES,
} from './sov.pdf.helpers';
import {
  OPTIONAL_SOV_COLUMN_OPTIONS,
} from '../ScheduleOfValuesColumns';

import { getCompanyImageURL } from '../../../settings/state/settings.actions';
import OnTraccrSwitch from '../../../common/inputs/OnTraccrSwitch';
import ScheduleOfValuesInputField from '../ScheduleOfValuesInputField';
import {
  updateIndividualProject,
  updateProjectScheduleOfValuePDFRows,
} from '../../state/projects.actions';
import CustomConfirmModal from '../../../common/modals/CustomConfirmModal';

const baseSections = [
  {
    label: 'Base Contract',
    value: 'baseContract',
  }, {
    label: 'Sub-Contracts',
    value: 'subContract',
  }, {
    label: 'Changes',
    value: 'changeOrder',
  }, {
    label: 'Non-Holdback Items',
    value: 'nonHoldback',
  },
];

function ScheduleOfValuesPDFPreview({
  projectId,
  templateId,
  divisionId,
  baseContractValues = [],
  subContractValues = [],
  liveSummaryValues: [liveSummaryRow = {}] = [],
  subContractCOs = {},
  changeOrderValues = [],
  nonHoldbackValues = [],
  sectionValueMap,
  currentHoldbackPercentage = 0,
  visible,
  onClose,
  sovTaxPercentage,
  isInvoiceHoldback,
  setIsInvoiceHoldback,
}) {
  const dispatch = useDispatch();
  const companyImageURL = useSelector((state) => state.settings.company.companyImageURL);
  const divisionImageURL = useSelector((state) => state.settings.divisions?.[divisionId]?.imageURL);
  const scheduleOfValuePDFRows = useSelector((state) => (
    state.projects.scheduleOfValuePDFRows || []
  ));
  const scheduleOfValueSections = useSelector((state) => state.projects.scheduleOfValueSections);

  const [pdfDefinition, setPdfDefinition] = useState(null);
  const [isPDFImageOn, setIsPDFImageOn] = useState(false);
  const [isLandscapeOn, setIsLandscapeOn] = useState(false);
  const [selectedPDFSections, setSelectedPDFSections] = useState([]);
  const [isTaxEnabled, setIsTaxEnabled] = useState(!!sovTaxPercentage);
  const [selectedPDFColumns, setSelectedPDFColumns] = useState([]);

  const logoURL = divisionImageURL || companyImageURL;
  const pdfImageStorageKey = `scheduleofvalue-pdf-image-${projectId}`;
  const pdfLandscapeStorageKey = `scheduleofvalue-pdf-landscape-${projectId}`;

  const pdfRef = useRef(null);

  const onTaxToggled = async (checked) => {
    const result = await dispatch(updateIndividualProject({
      projectId,
      payload: {
        details: {
          sovTaxPercentage: checked ? 0.1 : null,
        },
      },
    }));

    if (!result) return;

    setIsTaxEnabled(checked);
  };

  useEffect(() => {
    if (sovTaxPercentage) setIsTaxEnabled(true);
  }, [sovTaxPercentage]);

  const pdfSectionOptions = useMemo(() => (
    baseSections.concat(scheduleOfValueSections.map((section) => ({
      label: section.name,
      value: section.id,
    })))
  ), [scheduleOfValueSections]);

  useEffect(() => {
    const existingPDFSections = globalThis.localStorage.getItem(`scheduleofvalue-pdf-sections-${projectId}`);
    if (existingPDFSections) {
      setSelectedPDFSections(JSON.parse(existingPDFSections));
      return;
    }

    // If no existing config set the default sections
    setSelectedPDFSections(pdfSectionOptions.map((section) => section.value));
  }, [projectId, pdfSectionOptions]);

  useEffect(() => {
    const existingPDFColumns = globalThis.localStorage.getItem(`scheduleofvalue-pdf-columns-${projectId}`);
    if (existingPDFColumns) {
      setSelectedPDFColumns(JSON.parse(existingPDFColumns));
    }
  }, [projectId]);

  useEffect(() => {
    dispatch(getCompanyImageURL());
  }, []);

  useEffect(() => {
    const existingPDFImage = globalThis.localStorage.getItem(pdfImageStorageKey);
    setIsPDFImageOn(existingPDFImage ? JSON.parse(existingPDFImage) : false);
  }, [pdfImageStorageKey]);

  useEffect(() => {
    const existingPDFLandscape = globalThis.localStorage.getItem(pdfLandscapeStorageKey);
    setIsLandscapeOn(existingPDFLandscape ? globalThis.JSON.parse(existingPDFLandscape) : false);
  }, [pdfLandscapeStorageKey]);

  const onPDFImageToggled = (e) => {
    setIsPDFImageOn(e);
    globalThis.localStorage.setItem(pdfImageStorageKey, JSON.stringify(e));
  };

  // Clamp any out-of-range positions before switching orientation
  const applyLandscapeToggle = useCallback(async (checked) => {
    const maxValues = checked ? LANDSCAPE_MAX_VALUES : PORTRAIT_MAX_VALUES;
    const rowsToFix = scheduleOfValuePDFRows.filter((row) => (
      (row.xPosition ?? 0) > maxValues.xPosition
      || (row.yPosition ?? 0) > maxValues.yPosition
    ));

    for (const row of rowsToFix) {
      const xPosition = Math.min(row.xPosition ?? 0, maxValues.xPosition);
      const yPosition = Math.min(row.yPosition ?? 0, maxValues.yPosition);
      // eslint-disable-next-line no-await-in-loop
      await dispatch(updateProjectScheduleOfValuePDFRows({
        projectId,
        templateId,
        rowId: row.id,
        payload: {
          key: row.key,
          value: row.value,
          xPosition,
          yPosition,
          spacing: row.spacing,
        },
      }));
    }

    setIsLandscapeOn(checked);
    globalThis.localStorage.setItem(pdfLandscapeStorageKey, JSON.stringify(checked));
  }, [
    scheduleOfValuePDFRows,
    projectId,
    templateId,
    pdfLandscapeStorageKey,
  ]);

  const onLandscapeToggled = (checked) => {
    const orientationLabel = checked ? 'landscape' : 'portrait';
    const maxValues = checked ? LANDSCAPE_MAX_VALUES : PORTRAIT_MAX_VALUES;

    CustomConfirmModal({
      title: `Switch to ${orientationLabel}?`,
      content: `Any field positions that exceed the ${orientationLabel} limits (X max ${maxValues.xPosition}, Y max ${maxValues.yPosition}) will be auto-fixed to the maximum before switching.`,
      okText: 'Switch',
      cancelText: 'Cancel',
      zIndex: 1100,
      onOk: () => applyLandscapeToggle(checked),
    });
  };

  useEffect(() => {
    if (visible && pdfDefinition && pdfRef.current) {
      const pdfDocGenerator = pdfMake.createPdf(pdfDefinition);
      pdfDocGenerator.getDataUrl((dataUrl) => {
        pdfRef.current.src = dataUrl;
      });
    }
  }, [visible, pdfDefinition]);

  useEffect(() => {
    const generatePDFDefinition = async () => {
      const newPdfDefinition = {
        ...getBasePdfDefinition(isLandscapeOn ? 'landscape' : 'portrait'),
        content: [],
      };

      const pdfHeader = {
        columns: [],
        style: 'pdfHeader',
      };

      if (isPDFImageOn && logoURL) {
        let image;
        try {
          image = await getBase64ImageFromURL(logoURL);
        } catch (e) {
          // silently fail
        }

        if (image) {
          pdfHeader.columns.push({
            image,
            width: 150,
            maxWidth: 150,
            maxHeight: 100,
          });
        }
      }

      if (scheduleOfValuePDFRows.length) {
        const topCluster = [];
        const bottomCluster = [];
        scheduleOfValuePDFRows.forEach((row) => {
          if (row.yPosition <= 175) {
            topCluster.push(row);
          } else {
            bottomCluster.push(row);
          }
        });
        const topMaxKeyWidth = topCluster.length > 0
          ? Math.max(...topCluster.map((row) => {
            const keyText = row.key || '';
            return calculateTextWidth(keyText, 10, true);
          }))
          : 0;
        const bottomMaxKeyWidth = bottomCluster.length > 0
          ? Math.max(...bottomCluster.map((row) => {
            const keyText = row.key || '';
            return calculateTextWidth(keyText, 10, true);
          }))
          : 0;
        const pdfHeaderRows = scheduleOfValuePDFRows.map((row) => {
          const baseXPosition = row.xPosition || 0;
          const spacing = row.spacing ?? 0;
          const keyText = row.key || '';
          const labelWidth = calculateTextWidth(keyText, 10);
          const maxKeyWidth = row.yPosition <= 175 ? topMaxKeyWidth : bottomMaxKeyWidth;
          const keyWidth = isNullOrUndefined(row.spacing) ? maxKeyWidth : labelWidth;
          const valueXPosition = baseXPosition + keyWidth + spacing;
          
          return [
            {
              text: keyText,
              bold: true,
              style: 'tableCell',
              absolutePosition: { x: baseXPosition, y: row.yPosition },
            },
            {
              text: row.value,
              style: 'tableCell',
              absolutePosition: { x: valueXPosition, y: row.yPosition },
            },
          ];
        });

        pdfHeader.columns.push({
          table: {
            body: [
              ...pdfHeaderRows,
            ],
            heights: scheduleOfValuePDFRows
              .filter((row) => row.yPosition <= 175)
              .map((row) => 10 * ((row.value || '').split('\n').length)),
          },
          layout: 'noBorders',
          style: 'pdfHeaderTable',
        });
      }

      newPdfDefinition.content.push(pdfHeader);

      const tableHeaders = [
        { text: 'Item #', style: 'tableHeader' },
        { text: 'Description', style: 'tableHeader' },
        { text: 'Contract Amount', style: 'tableHeader' },
        { text: '% Complete to this billing', style: 'tableHeader' },
        { text: 'Progress to Date', style: 'tableHeader' },
        { text: 'Previous Billings', style: 'tableHeader' },
        { text: 'This Invoice', style: 'tableHeader' },
      ];

      if (selectedPDFColumns.includes('unitType')) {
        tableHeaders.push({ text: 'Unit Type', style: 'tableHeader' });
      }
      if (selectedPDFColumns.includes('estimatedQuantity')) {
        tableHeaders.push({ text: 'Est. Quantity', style: 'tableHeader' });
      }
      if (selectedPDFColumns.includes('previouslyBilledQuantity')) {
        tableHeaders.push({ text: 'Previously Billed Qty', style: 'tableHeader' });
      }
      if (selectedPDFColumns.includes('billableQuantity')) {
        tableHeaders.push({ text: 'Billable Qty', style: 'tableHeader' });
      }
      if (selectedPDFColumns.includes('completedQuantity')) {
        tableHeaders.push({ text: 'Completed Quantity', style: 'tableHeader' });
      }
      if (selectedPDFColumns.includes('quantityToDate')) {
        tableHeaders.push({ text: 'Quantity To Date', style: 'tableHeader' });
      }
      if (selectedPDFColumns.includes('quantityRemaining')) {
        tableHeaders.push({ text: 'Quantity Remaining', style: 'tableHeader' });
      }
      if (selectedPDFColumns.includes('billingCode')) {
        tableHeaders.push({ text: 'Billing Code', style: 'tableHeader' });
      }

      // Do a deep copy to get table headers since pdfmake alters the columns
      const changeOrderTableHeaders = JSON.parse(
        JSON.stringify(tableHeaders.toSpliced(1, 0, { text: 'Number', style: 'tableHeader' })),
      );

      const nonHoldbackTableHeaders = JSON.parse(
        JSON.stringify(tableHeaders),
      );

      const selectedPDFSectionSet = new Set(selectedPDFSections);

      const showBaseContract = baseContractValues.length && selectedPDFSectionSet.has('baseContract');
      const showSubContract = subContractValues.length && selectedPDFSectionSet.has('subContract');
      const showNonHoldback = selectedPDFSectionSet.has('nonHoldback');
      const showChanges = selectedPDFSectionSet.has('changeOrder');
      const displaySections = [];

      scheduleOfValueSections.forEach((section) => {
        if (selectedPDFSectionSet.has(section.id) && sectionValueMap[section.id]?.length) {
          displaySections.push(section);
        }
      });

      if (
        (
          showBaseContract
          || showSubContract
          || displaySections?.length
        )
      ) {
        const baseTable = {
          headerRows: 1,
          body: [],
          widths: buildTableWidths(false, selectedPDFColumns.length),
        };

        let baseTableRows = [];

        const optionalEmptyCells = selectedPDFColumns.map(() => ({ text: '' }));

        if (showSubContract) {
          baseTableRows = baseTableRows.concat(subContractValues.map((c) => getTableRow(c, false, selectedPDFColumns)));
          const totalSubContractText = currencyFormatter(liveSummaryRow.totalSubContractAmount);
          const subProgressText = currencyFormatter(liveSummaryRow.subContractProgressToDate);
          const subPreviousText = currencyFormatter(liveSummaryRow.subContractPreviousBillings);
          const subInvoiceText = currencyFormatter(liveSummaryRow.subContractInvoiceAmount);

          baseTableRows.push([
            {
              text: 'TOTAL SUB-CONTRACTS',
              style: 'tableHeader',
              alignment: 'right',
              colSpan: 2,
              noWrap: true,
            },
            {},
            {
              text: totalSubContractText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(totalSubContractText),
              noWrap: true,
            },
            {
              text: `${(
                liveSummaryRow.totalSubContractAmount
                  ? (
                    (liveSummaryRow.subContractProgressToDate
                      / liveSummaryRow.totalSubContractAmount)
                    * 100
                  )
                  : 0
              ).toFixed(2)}%`,
              style: 'tableHeader',
            },
            {
              text: subProgressText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(subProgressText),
              noWrap: true,
            },
            {
              text: subPreviousText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(subPreviousText),
              noWrap: true,
            },
            {
              text: subInvoiceText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(subInvoiceText),
              noWrap: true,
            },
            ...optionalEmptyCells,
          ]);
        }

        if (showBaseContract) {
          baseTableRows = baseTableRows.concat(
            getSovTableRows(baseContractValues, false, selectedPDFColumns),
          );
          const totalContractText = currencyFormatter(liveSummaryRow.totalContractAmount);
          const baseProgressText = currencyFormatter(liveSummaryRow.baseContractProgressToDate);
          const basePreviousText = currencyFormatter(liveSummaryRow.baseContractPreviousBillings);
          const baseInvoiceText = currencyFormatter(liveSummaryRow.baseContractInvoiceAmount);

          baseTableRows.push([
            {
              text: 'TOTAL BASE CONTRACT',
              style: 'tableHeader',
              alignment: 'right',
              colSpan: 2,
              noWrap: true,
            },
            {},
            {
              text: totalContractText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(totalContractText),
              noWrap: true,
            },
            {
              text: `${(
                liveSummaryRow.totalContractAmount
                  ? (
                    (liveSummaryRow.baseContractProgressToDate / liveSummaryRow.totalContractAmount)
                    * 100
                  )
                  : 0
              ).toFixed(2)}%`,
              style: 'tableHeader',
            },
            {
              text: baseProgressText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(baseProgressText),
              noWrap: true,
            },
            {
              text: basePreviousText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(basePreviousText),
              noWrap: true,
            },
            {
              text: baseInvoiceText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(baseInvoiceText),
              noWrap: true,
            },
            ...optionalEmptyCells,
          ]);
        }

        if (displaySections) {
          displaySections.forEach((section) => {
            if (!sectionValueMap[section.id]?.length) return;
            const sectionRows = sectionValueMap[section.id];
            baseTableRows = baseTableRows.concat(
              getSovTableRows(sectionRows, false, selectedPDFColumns),
            );
            const sectionContractAmount = sectionRows.reduce((acc, row) => (
              acc + row.contractAmount
            ), 0);
            const sectionContractText = currencyFormatter(sectionContractAmount);
            const sectionProgressText = currencyFormatter(liveSummaryRow[`${section.id}ProgressToDate`]);
            const sectionPreviousText = currencyFormatter(liveSummaryRow[`${section.id}PreviousBillings`]);
            const sectionInvoiceText = currencyFormatter(liveSummaryRow[`${section.id}InvoiceAmount`]);

            baseTableRows.push([
              {
                text: `TOTAL ${section.name.toUpperCase()}`,
                style: 'tableHeader',
                alignment: 'right',
                colSpan: 2,
                noWrap: true,
              },
              {},
              {
                text: sectionContractText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(sectionContractText),
                noWrap: true,
              },
              {
                text: `${(
                  sectionContractAmount
                    ? (
                      (liveSummaryRow[`${section.id}ProgressToDate`] / sectionContractAmount)
                      * 100
                    )
                    : 0
                ).toFixed(2)}%`,
                style: 'tableHeader',
              },
              {
                text: sectionProgressText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(sectionProgressText),
                noWrap: true,
              },
              {
                text: sectionPreviousText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(sectionPreviousText),
                noWrap: true,
              },
              {
                text: sectionInvoiceText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(sectionInvoiceText),
                noWrap: true,
              },
              ...optionalEmptyCells,
            ]);
          });
        }

        if (currentHoldbackPercentage && showNonHoldback) {
          baseTableRows.push(getNonHoldbackSummary(liveSummaryRow, selectedPDFColumns));
        }

        if (showChanges) baseTableRows.push(getChangeOrderSummary(liveSummaryRow, false, selectedPDFColumns));

        const contractTotalText = currencyFormatter(liveSummaryRow.totalContractValue);
        const totalProgressText = currencyFormatter(
          liveSummaryRow.changeOrderProgressToDate
            + liveSummaryRow.baseContractProgressToDate
            + liveSummaryRow.subContractProgressToDate
            + liveSummaryRow.nonHoldbackProgressToDate,
        );
        const totalBilledText = currencyFormatter(liveSummaryRow.totalBilledToDate);
        const totalInvoiceText = currencyFormatter(liveSummaryRow.invoiceAmount);

        baseTableRows.push([
          {
            text: 'CONTRACT TOTAL',
            style: 'tableHeader',
            alignment: 'right',
            colSpan: 2,
          },
          {},
          {
            text: contractTotalText,
            style: 'tableHeader',
            alignment: 'left',
            fontSize: getCurrencyFontSize(contractTotalText),
            noWrap: true,
          },
          {
            text: `${(
              liveSummaryRow.totalContractValue ? (
                ((
                  liveSummaryRow.changeOrderProgressToDate
                  + liveSummaryRow.baseContractProgressToDate
                  + liveSummaryRow.subContractProgressToDate
                  + liveSummaryRow.nonHoldbackProgressToDate
                ) / liveSummaryRow.totalContractValue) * 100
              ) : 0
            ).toFixed(2)}%`,
            style: 'tableHeader',
          },
          {
            text: totalProgressText,
            style: 'tableHeader',
            alignment: 'left',
            fontSize: getCurrencyFontSize(totalProgressText),
            noWrap: true,
          },
          {
            text: totalBilledText,
            style: 'tableHeader',
            alignment: 'left',
            fontSize: getCurrencyFontSize(totalBilledText),
            noWrap: true,
          },
          {
            text: totalInvoiceText,
            style: 'tableHeader',
            alignment: 'left',
            fontSize: getCurrencyFontSize(totalInvoiceText),
            noWrap: true,
          },
          ...optionalEmptyCells,
        ]);

        baseTable.body.push(tableHeaders, ...baseTableRows);
        newPdfDefinition.content.push({
          table: baseTable,
        });
      }

      if (
        (baseContractValues.length
          || changeOrderValues.length
          || subContractValues.length
          || Object.values(sectionValueMap).length > 0
        )
      ) {
        const footerSummary = {
          columns: [
            { width: '*', text: '' },
          ],
        };

        const {
          relevantCustomSectionProgressToDate = 0,
          relevantCustomSectionInvoiceAmount = 0,
        } = scheduleOfValueSections.reduce((acc, section) => {
          if (section.shouldBeInCalculations) {
            acc.relevantCustomSectionProgressToDate += liveSummaryRow[`${section.id}ProgressToDate`] ?? 0;
            acc.relevantCustomSectionInvoiceAmount += liveSummaryRow[`${section.id}InvoiceAmount`] ?? 0;
          }

          return acc;
        }, {
          relevantCustomSectionProgressToDate: 0,
          relevantCustomSectionInvoiceAmount: 0,
        });

        const totalCompletedText = currencyFormatter(
          liveSummaryRow.changeOrderProgressToDate
            + liveSummaryRow.baseContractProgressToDate
            + liveSummaryRow.subContractProgressToDate
            + liveSummaryRow.nonHoldbackProgressToDate
            + relevantCustomSectionProgressToDate,
        );
        const lessPreviousText = currencyFormatter(liveSummaryRow.totalBilledToDate);
        const holdbackExcludedText = currencyFormatter(liveSummaryRow.nonHoldbackInvoiceAmount);
        const holdbackIncludedText = currencyFormatter(
          liveSummaryRow.baseContractInvoiceAmount
            + liveSummaryRow.changeOrderInvoiceAmount
            + liveSummaryRow.subContractInvoiceAmount
            + relevantCustomSectionInvoiceAmount,
        );
        const totalThisBillingText = currencyFormatter(liveSummaryRow.invoiceAmount);

        const footerRows = [
          [
            { text: '' },
            { text: 'Summary', style: 'tableHeader', color: 'red' },
            { text: '' },
          ],
        ];

        if (!isInvoiceHoldback) {
          footerRows.push(...[
            [
              { text: '' },
              { text: 'Total completed to date:', style: 'tableHeader', alignment: 'right' },
              {
                text: totalCompletedText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(totalCompletedText),
                noWrap: true,
              },
            ],
            [
              { text: '' },
              { text: 'Less previous billings:', style: 'tableHeader', alignment: 'right' },
              {
                text: lessPreviousText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(lessPreviousText),
                noWrap: true,
              },
            ],
            [
              { text: '' },
              { text: 'Holdback Excluded Billing:', style: 'tableHeader', alignment: 'right' },
              {
                text: holdbackExcludedText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(holdbackExcludedText),
                noWrap: true,
              },
            ],
            [
              { text: '' },
              { text: 'Holdback Included Billing:', style: 'tableHeader', alignment: 'right' },
              {
                text: holdbackIncludedText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(holdbackIncludedText),
                noWrap: true,
              },
            ],
            [
              { text: '' },
              { text: 'Total this billing:', style: 'tableHeader', alignment: 'right' },
              {
                text: totalThisBillingText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(totalThisBillingText),
                noWrap: true,
              },
            ],
          ]);
        } else {
          const holdbackAmountText = currencyFormatter(liveSummaryRow.holdbackAmount);
          footerRows.push(...[
            [
              { text: '' },
              { text: 'Total Billed to Date:', style: 'tableHeader', alignment: 'right' },
              {
                text: lessPreviousText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(lessPreviousText),
                noWrap: true,
              },
            ],
            [
              { text: '' },
              { text: `Holdback Invoice ${isTaxEnabled ? 'Subtotal' : 'Total'}:`, style: 'tableHeader', alignment: 'left' },
              {
                text: holdbackAmountText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(holdbackAmountText),
                noWrap: true,
              },
            ],
          ]);
        }

        if (currentHoldbackPercentage && !isInvoiceHoldback) {
          const holdbackAmount = roundToCents(
            liveSummaryRow.holdbackPercentage * (
              liveSummaryRow.baseContractInvoiceAmount
              + liveSummaryRow.changeOrderInvoiceAmount
              + liveSummaryRow.subContractInvoiceAmount
              + relevantCustomSectionInvoiceAmount
            ),
          );
          const holdbackAmountText = currencyFormatter(holdbackAmount);
          footerRows.push([
            { text: 'Less holdback:', style: 'tableHeader', alignment: 'right' },
            {
              text: `${(liveSummaryRow.holdbackPercentage * 100).toFixed(2)}%`,
              style: 'tableHeader',
              fillColor: 'yellow',
            },
            {
              text: holdbackAmountText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(holdbackAmountText),
              noWrap: true,
            },
          ]);
        }


        const invoiceTitle = isTaxEnabled ? 'Invoice Sub-Total' : 'Invoice Total';

        const invoiceTotal = roundToCents(
          // eslint-disable-next-line max-len
          isInvoiceHoldback
            ? liveSummaryRow.holdbackAmount
            : liveSummaryRow.invoiceAmount - (liveSummaryRow.holdbackPercentage * (liveSummaryRow.invoiceAmount - liveSummaryRow.nonHoldbackInvoiceAmount)),
        );
        const invoiceTotalText = currencyFormatter(invoiceTotal);

        if (!isInvoiceHoldback) {
          footerRows.push([
            { text: '' },
            { text: `${invoiceTitle}:`, style: 'tableHeader', alignment: 'right' },
            {
              text: invoiceTotalText,
              style: 'tableHeader',
              alignment: 'left',
              fontSize: getCurrencyFontSize(invoiceTotalText),
              noWrap: true,
            },
          ]);
        }

        if (isTaxEnabled) {
          const taxTotal = roundToCents(invoiceTotal * (sovTaxPercentage));
          const formattedTax = (sovTaxPercentage * 100);
          const roundedTax = formattedTax !== parseInt(formattedTax, 10)
            ? (sovTaxPercentage * 100).toFixed(1)
            : formattedTax;
          const taxTotalText = currencyFormatter(taxTotal);
          const finalInvoiceTotalText = currencyFormatter(invoiceTotal + taxTotal);

          footerRows.push(
            [
              { text: '' },
              { text: 'Tax Percent:', style: 'tableHeader', alignment: 'right' },
              { text: percentFormatter(roundedTax), style: 'tableHeader', alignment: 'left' },
            ],
            [
              { text: '' },
              { text: 'Tax Total:', style: 'tableHeader', alignment: 'right' },
              {
                text: taxTotalText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(taxTotalText),
                noWrap: true,
              },
            ],
            [
              { text: '' },
              { text: 'Invoice Total:', style: 'tableHeader', alignment: 'right' },
              {
                text: finalInvoiceTotalText,
                style: 'tableHeader',
                alignment: 'left',
                fontSize: getCurrencyFontSize(finalInvoiceTotalText),
                noWrap: true,
              },
            ],
          );
        }

        footerSummary.columns.push({
          width: 'auto',
          unbreakable: true,
          layout: 'noBorders',
          table: {
            body: footerRows,
          },
        });

        newPdfDefinition.content.push(footerSummary);
      }

      if (
        (changeOrderValues.length || Object.keys(subContractCOs).length)
        && showChanges
      ) {
        const changeOrderTable = {
          headerRows: 1,
          body: [],
          widths: buildTableWidths(true, selectedPDFColumns.length),
        };

        const coNumCols = 8 + selectedPDFColumns.length;
        let changeOrderRows = [];

        subContractValues.forEach((subContract) => {
          const { rowId, description } = subContract;
          const {
            [rowId]: {
              changes: subContractChanges = [],
            } = {},
          } = subContractCOs;
          if (subContractChanges.length === 0) return;
          changeOrderRows.push(getSubContractCOHeader(description, coNumCols));
          changeOrderRows = changeOrderRows.concat(
            subContractChanges.map((c) => getTableRow(c, true, selectedPDFColumns)),
          );
        });

        if (changeOrderRows.length) {
          changeOrderRows.push(getSubContractCOHeader('CHANGES', coNumCols));
        }

        changeOrderRows = changeOrderRows.concat(
          changeOrderValues.map((c) => getTableRow(c, true, selectedPDFColumns)),
        );
        changeOrderRows.push(
          getChangeOrderSummary(liveSummaryRow, true, selectedPDFColumns),
        );

        changeOrderTable.body.push(changeOrderTableHeaders, ...changeOrderRows);
        newPdfDefinition.content.push({
          table: changeOrderTable,
          pageBreak: 'before',
        });
      }

      if (
        nonHoldbackValues.length
        && currentHoldbackPercentage
        && showNonHoldback
      ) {
        const nonHoldbackTable = {
          headerRows: 1,
          body: [],
          widths: buildTableWidths(false, selectedPDFColumns.length),
        };

        const nonHoldbackRows = nonHoldbackValues.map((c) => getTableRow(c, false, selectedPDFColumns));
        nonHoldbackRows.push(
          getNonHoldbackSummary(liveSummaryRow, selectedPDFColumns),
        );

        nonHoldbackTable.body.push(nonHoldbackTableHeaders, ...nonHoldbackRows);
        newPdfDefinition.content.push({
          table: nonHoldbackTable,
          pageBreak: 'before',
        });
      }

      setPdfDefinition(newPdfDefinition);
    };

    generatePDFDefinition();
  }, [
    baseContractValues,
    subContractValues,
    changeOrderValues,
    liveSummaryRow,
    nonHoldbackValues,
    scheduleOfValuePDFRows,
    currentHoldbackPercentage,
    isPDFImageOn,
    isLandscapeOn,
    logoURL,
    selectedPDFSections,
    selectedPDFColumns,
    isTaxEnabled,
    sovTaxPercentage,
    isInvoiceHoldback,
  ]);

  const onCloseDrawer = useCallback(() => {
    setIsInvoiceHoldback(false);
    onClose();
  }, [onClose, setIsInvoiceHoldback]);

  return (
    <Drawer
      title="Review PDF"
      visible={visible}
      onClose={onCloseDrawer}
      width={1100}
      zIndex={1049}
    >
      <Form.Item>
        <OnTraccrSwitch
          value={isPDFImageOn}
          onChange={onPDFImageToggled}
          title={divisionImageURL ? 'Load in division image' : 'Load in company image'}
        />
      </Form.Item>
      <Form.Item>
        <OnTraccrSwitch
          value={isLandscapeOn}
          onChange={onLandscapeToggled}
          title="Use landscape orientation"
        />
      </Form.Item>
      <Row gutter={14} align="middle" style={{ marginBottom: 10 }}>
        <Col>
          <OnTraccrSwitch
            value={isTaxEnabled}
            onChange={onTaxToggled}
            title="Add Tax to Invoice Total"
          />
        </Col>
        {isTaxEnabled && (
          <Col>
            <ScheduleOfValuesInputField
              min={0}
              max={100}
              field="sovTaxPercentage"
              type="number"
              subType="percentage"
              formatter={(val) => `${val} %`}
              title="Tax Rate"
              sovTaxPercentage={sovTaxPercentage}
              precision={1}
              projectId={projectId}
            />
          </Col>
        )}
      </Row>
      <Form.Item name="pdfSections" label="PDF Sections">
        <Select
          mode="multiple"
          defaultValue={selectedPDFSections}
          value={selectedPDFSections}
          onChange={(value) => {
            setSelectedPDFSections(value);
            globalThis.localStorage.setItem(`scheduleofvalue-pdf-sections-${projectId}`, JSON.stringify(value));
          }}
          options={pdfSectionOptions}
        />
      </Form.Item>
      <Form.Item name="pdfColumns" label="Optional Columns">
        <Select
          mode="multiple"
          value={selectedPDFColumns}
          onChange={(value) => {
            setSelectedPDFColumns(value);
            globalThis.localStorage.setItem(`scheduleofvalue-pdf-columns-${projectId}`, JSON.stringify(value));
          }}
          options={OPTIONAL_SOV_COLUMN_OPTIONS}
          placeholder="No optional columns"
        />
      </Form.Item>
      <ScheduleOfValuesPDFPreviewForm
        projectId={projectId}
        templateId={templateId}
        isLandscapeOn={isLandscapeOn}
      />
      <iframe title="Contract PDF" ref={pdfRef} className="sov-pdf-preview-frame" />
    </Drawer>
  );
}

/* eslint-disable react/forbid-prop-types */
ScheduleOfValuesPDFPreview.propTypes = {
  projectId: PropTypes.string,
  templateId: PropTypes.string,
  divisionId: PropTypes.string,
  baseContractValues: PropTypes.array.isRequired,
  subContractValues: PropTypes.array.isRequired,
  liveSummaryValues: PropTypes.array.isRequired,
  changeOrderValues: PropTypes.array.isRequired,
  nonHoldbackValues: PropTypes.array.isRequired,
  currentHoldbackPercentage: PropTypes.number.isRequired,
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  subContractCOs: PropTypes.object.isRequired,
  sovTaxPercentage: PropTypes.number,
  isInvoiceHoldback: PropTypes.bool,
  setIsInvoiceHoldback: PropTypes.func,
};

ScheduleOfValuesPDFPreview.defaultProps = {
  visible: false,
  projectId: null,
  templateId: null,
  divisionId: null,
  sovTaxPercentage: null,
  isInvoiceHoldback: false,
  setIsInvoiceHoldback: () => {},
};

export default ScheduleOfValuesPDFPreview;
